import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function TenderFinished({ tenderTypeId, mode, roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Keep identity stable to avoid unnecessary re-renders/refetches in MTable
    const removeContextItems = useMemo(() => ['Print', 'Copy', 'Create', 'Edit', 'Delete'], []);

    // Constant for the CRUD route
    const CRUD_ROUTE = 'tenders/TenderFinished';

    //============================================================================================================
    // Function to handle the creation of a new route based on the operation and data
    //============================================================================================================
    const NewRoute = (operation, data) => {
        if (!data) { // If no data is provided, show an error message, since we need a selected line (tender) to analyse
            toastMessages('error', '', t("SelectedLine"));
            return;
        }
        dispatch(M_StateActions.addM_State({ key: 'operation', value: operation })); // Store the operation in the state
        dispatch(M_StateActions.addM_State({ key: 'TenderLine', value: data })); // Store the selected tender line in the state

        const route = 'tenders/TenderFinished';
        // Use the name of the tender or a default label if not available
        const label = `${t("LabelFinishedTender")} "${data?.RequestForQuotes?.Name || data?.RequestForQuotes || 'Tender'}"`; 
        onCreateTab(route, label);
    };

    
    //========================================================================================
    // openFinishedTender:
    // - Stable (useCallback) wrapper that delegates to NewRoute.
    // - Keeps a consistent navigation flow and persists the selected tender/operation.
    // - Using a stable reference prevents MTable from rebuilding menus or refetching.
    //========================================================================================
    const openFinishedTender = useCallback(
        (operation, row) => {
            NewRoute(operation, row);
        },
        // NewRoute closes over t/dispatch/onCreateTab/toastMessages, so we only list NewRoute.
        [NewRoute]
    );

    //========================================================================================
    // replaceFunctions:
    // - Memoized Map passed to MTable to resolve context actions without recreating on each render.
    // - Uses the stable openFinishedTender callback and localized labels.
    // - Stable identity reduces unnecessary re-renders and duplicate API calls downstream.
    //========================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openFinishedTender('View', row)],
            ]),
        [openFinishedTender]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>
                        {mode === 'spot' ? t("SpotTendersFinished") : t("NonSpotTendersFinished")}
                    </div>
                    <div className='labelSubtitleSettings'>
                        {mode === 'spot' ? t("InfoSpotTendersFinished") : t("InfoNonSpotTendersFinished")}
                    </div>
                </div>
            </div>

            <MTable // Dynamic Table for Finished Tenders
                filtersOnTable
                entityName={'Tenders'} // Fetch the Tender table
                roleName={'RequestForQuotes'} // Permissions are based on RequestForQuotes roles
                rolePermissions={['Read', 'View']} // Only Read and View permissions are allowed in this page
                removeContextItems={removeContextItems} // Remove unnecessary context menu items
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                FILTER={`TenderTypeId eq ${tenderTypeId} and IsFinished eq true`} // Filter to show only finished tenders of the specified type ID and already finished
                replaceFunctions={replaceFunctions} // Use the memoized replaceFunctions for context actions
                tabsRailWidth={250}
            />
        </div>
    );
}

TenderFinished.propTypes = {
    tenderTypeId: PropTypes.number.isRequired,
    mode: PropTypes.oneOf(['spot', 'nonspot']),
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
